import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateJob } from "@/lib/api/hooks";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/lyrics")({
  component: LyricsDeckPage,
  head: () => ({ meta: [{ title: "Lyrics deck — CSaaS" }] }),
});

// The ported lsc-pptx lyrics → deck skills. Deterministic skills run with no
// LLM; the agentic ones (auto-populate / translate) use the Claude Agent SDK.
const LYRICS_SKILLS = [
  {
    name: "lyrics-md-to-pptx",
    label: "Generate deck (.pptx)",
    needsTemplate: true,
    blurb: "Deterministic — build a slide deck from a complete lyrics.md.",
  },
  {
    name: "validate-lyrics-md",
    label: "Validate lyrics.md",
    needsTemplate: false,
    blurb: "Deterministic — check LSC policy (zh+en present, 3-line groups).",
  },
  {
    name: "prep-lyrics-md",
    label: "Auto-populate lyrics (web research)",
    needsTemplate: false,
    blurb: "Agentic — fetch YouTube metadata + CCLI/SOP lyrics to fill the file.",
  },
  {
    name: "translate-lyrics",
    label: "Fill missing language",
    needsTemplate: false,
    blurb: "Agentic — complete the missing pinyin/Chinese/English column.",
  },
] as const;

// Templates vendored into the worker (worker/skill_runtimes/lsc_pptx/templates).
const TEMPLATES = [{ value: "blue", label: "Blue (LSC default)" }] as const;

const SAMPLE = `## 1) Song Title

**Title:** pīnyīn / 中文詩歌名 / ENGLISH NAME
**Artist:** Artist
**CCLI #:** 1234567
**HasLyrics:** yes
**Duration:** 4:00

[Video](https://youtu.be/VIDEO_ID)

### Verse 1

pīnyīn line
中文歌詞
ENGLISH LINE
`;

function LyricsDeckPage() {
  const navigate = useNavigate();
  const createJob = useCreateJob();

  const [skillName, setSkillName] = useState<string>(LYRICS_SKILLS[0].name);
  const [template, setTemplate] = useState<string>(TEMPLATES[0].value);
  const [md, setMd] = useState("");

  const skill = LYRICS_SKILLS.find((s) => s.name === skillName) ?? LYRICS_SKILLS[0];

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!md.trim()) {
      toast.error("Paste a lyrics.md first.");
      return;
    }
    createJob.mutate(
      {
        skillName: skill.name,
        prompt: "",
        inputFiles: { "lyrics.md": md },
        context: skill.needsTemplate ? { template } : null,
        // Agentic web-research/translation can take a while; deterministic
        // generation is fast but the ceiling is harmless.
        timeoutSeconds: skill.name === "prep-lyrics-md" ? 900 : 300,
      },
      {
        onSuccess: (job) => {
          toast.success("Job queued");
          navigate({ to: "/jobs/$id", params: { id: job.id } });
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">Lyrics deck</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Generate, validate, populate, or translate a worship lyrics file (lsc-pptx pipeline).
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Lyrics → deck</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label>Action</Label>
              <Select value={skillName} onValueChange={setSkillName}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LYRICS_SKILLS.map((s) => (
                    <SelectItem key={s.name} value={s.name}>
                      <span className="font-medium">{s.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{skill.blurb}</p>
            </div>

            {skill.needsTemplate && (
              <div className="space-y-2">
                <Label>Template</Label>
                <Select value={template} onValueChange={setTemplate}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TEMPLATES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="md">lyrics.md</Label>
                <button
                  type="button"
                  className="text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => setMd(SAMPLE)}
                >
                  Insert sample
                </button>
              </div>
              <Textarea
                id="md"
                rows={16}
                placeholder={SAMPLE}
                value={md}
                onChange={(e) => setMd(e.target.value)}
                className="font-mono text-xs"
              />
            </div>

            <Button type="submit" disabled={!md.trim() || createJob.isPending}>
              {createJob.isPending ? "Submitting…" : "Run"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
